import {FC, useEffect, useMemo, useMemo as useMemoHook, useState} from 'react';
import DataTable, {TableColumn} from 'react-data-table-component';
import {toRem} from 'shared/helpers';
import {useResize} from 'app/lib/hooks/useResize';
import {
  DeviceRemoveContent,
  DeviceRemoveContentColButtons,
  DeviceRemoveContentColF,
  DevicesEmptyState,
  DevicesEmptyStateTitle,
  DevicesTableWrapper,
} from './styles';
import {DeviceRow} from 'entities/devices/model/types';
import {BatteryBadge} from '../components/BatteryBadge';
import {DisableButton} from '../components/DisableButton';
import {formatDistanceToNowStrict} from 'date-fns';
import {ru} from 'date-fns/locale';
import {DevicesMobile} from './DevicesMobile';
import {useDevicesTable} from '../../lib/useDevicesTable';
import HiveCol from 'features/devices/ui/components/HiveCol';
import Modal from '@atomaro/ui-kit/components/Modal/Modal';
import {Button} from '@atomaro/ui-kit';
import {toast} from 'react-toastify';
import {useDevicesStore} from 'entities/devices/model/store';
import {useNewDeviceModalStore} from 'entities/newDeviceModal';
import {AddLarge} from 'shared/ui/icons/addLarge';
import {useApiariesStore} from 'entities/apiaries';
import {useHivesStore} from 'entities/hives';
import {HivesRow} from 'features/hives';
import Input from '@atomaro/ui-kit/components/Input/Input';
import {SearchIcon} from 'shared/ui/icons/searchIcon';
import {HiveIcon} from 'shared/ui/icons/hiveIcon';
import styled from 'styled-components';
import {COLORS} from 'shared/consts';

const ConnectDeviceModalWrapper = styled.div`
  padding: ${toRem(16)};
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: ${toRem(600)};
  min-height: ${toRem(400)};
`;

const ConnectDeviceModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${toRem(16)};

  h3 {
    margin: 0;
    color: ${COLORS.FG.DEFAULT};
    font-size: ${toRem(16)};
    font-weight: 700;
    line-height: ${24 / 16};
  }

  svg {
    cursor: pointer;
  }
`;

const ConnectDeviceModalSubtitle = styled.p`
  margin: 0 0 ${toRem(16)} 0;
  color: ${COLORS.FG.DEFAULT};
  font-size: ${toRem(14)};
  font-weight: 400;
  line-height: ${20 / 14};
`;

const SearchInputWrapper = styled.div`
  margin-bottom: ${toRem(16)};
  position: relative;

  svg {
    position: absolute;
    left: ${toRem(12)};
    top: 50%;
    transform: translateY(-50%);
    width: ${toRem(20)};
    height: ${toRem(20)};
    pointer-events: none;
    z-index: 1;
  }

  input {
    padding-left: ${toRem(40)} !important;
  }
`;

const HivesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${toRem(4)};
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  margin-bottom: ${toRem(16)};
`;

const HiveItem = styled.div<{$selected: boolean}>`
  display: flex;
  align-items: center;
  gap: ${toRem(12)};
  padding: ${toRem(12)} ${toRem(16)};
  background: ${({$selected}) => ($selected ? COLORS.ACCENT.CONTAINER_SOFT : COLORS.BG.SURFACE1)};
  border-radius: ${toRem(8)};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({$selected}) => ($selected ? COLORS.ACCENT.CONTAINER_SOFT : COLORS.BG.SURFACE2)};
  }
`;

const HiveItemIcon = styled.div`
  width: ${toRem(24)};
  height: ${toRem(24)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const HiveItemText = styled.div<{$selected: boolean}>`
  flex: 1;
  color: ${({$selected}) => ($selected ? COLORS.ACCENT.DEFAULT : COLORS.FG.DEFAULT)};
  font-size: ${toRem(14)};
  font-weight: 500;
  line-height: ${20 / 14};
`;

const EmptyState = styled.div`
  padding: ${toRem(40)} ${toRem(20)};
  text-align: center;
  color: ${COLORS.FG.MUTED};
  font-size: ${toRem(14)};
  line-height: ${20 / 14};
`;

const ModalFooter = styled.div`
  display: flex;
  gap: ${toRem(12)};
  justify-content: flex-end;
  padding-top: ${toRem(16)};
  border-top: 1px solid ${COLORS.BORDER.SOFT};
  margin-top: auto;
`;

const EmptyStateComponent = ({onOpenModal}: {onOpenModal: () => void}) => {
  return (
    <DevicesEmptyState>
      <DevicesEmptyStateTitle>Добавьте устройство</DevicesEmptyStateTitle>
      <Button
        size="m"
        colorScheme="accent"
        label="Добавить устройство"
        iconPrefix={<AddLarge fill="white" />}
        onClick={onOpenModal}
      />
    </DevicesEmptyState>
  );
};

export const DevicesTable: FC = () => {
  const {isScreenLg} = useResize();
  const {rows, customStyles} = useDevicesTable();
  const detachDevice = useDevicesStore((s) => s.detachDevice);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const openNewDeviceModal = useNewDeviceModalStore((s) => s.open);
  const apiaryId = useApiariesStore((s) => s.currentApiaryId);
  const getHivesFromServer = useHivesStore((s) => s.getHivesFromServer);
  const hives = useHivesStore((s) => s.hives);
  const isLoading = useHivesStore((s) => s.isLoading);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHive, setSelectedHive] = useState<HivesRow | null>(null);

  useEffect(() => {
    if (isConnectModalOpen && apiaryId) {
      getHivesFromServer({apiaryId, page: {index: 0, size: 200}});
    }
  }, [isConnectModalOpen, apiaryId, getHivesFromServer]);

  useEffect(() => {
    if (!isConnectModalOpen) {
      setSearchQuery('');
      setSelectedHive(null);
    }
  }, [isConnectModalOpen]);

  const filteredHives = useMemoHook(() => {
    if (!searchQuery.trim()) return hives;
    const query = searchQuery.toLowerCase();
    return hives.filter((hive) => String(hive.number).toLowerCase().includes(query));
  }, [hives, searchQuery]);

  const handleHiveClick = (hive: HivesRow) => {
    setSelectedHive(hive);
  };

  const handleNext = () => {
    if (selectedHive) {
      openNewDeviceModal(selectedHive.id, String(selectedHive.number));
      setIsConnectModalOpen(false);
    }
  };

  const columns = useMemo<TableColumn<DeviceRow>[]>(
    () => [
      {
        name: 'Устройство',
        grow: 1,
        selector: (r) => r.name || 'Устройство без названия',
        minWidth: toRem(180),
        sortable: true,
      },
      {
        name: 'Подключено к улью',
        grow: 1,
        selector: (r) => (<HiveCol id={r.hiveId || ''} name={r.hiveTitle} />) as any,
        minWidth: toRem(160),
        sortable: true,
      },
      {
        name: 'Заряд батареи',
        grow: 1,
        cell: (r) => <BatteryBadge voltage={r.batteryVoltage} />,
        sortable: true,
      },
      {
        name: 'Обновлено',
        selector: (r) =>
          (
            <span style={{color: 'var(--fg-muted, rgba(16, 24, 40, 0.55))'}}>
              {r.updatedISO
                ? formatDistanceToNowStrict(new Date(r.updatedISO), {locale: ru, addSuffix: true})
                : ''}
            </span>
          ) as any,
        width: toRem(140),
        sortable: true,
      },
      {
        name: '',
        cell: (r) => (
          <DisableButton openModal={(id: string) => setIsOpenRemoveModal(id)} id={r.id} />
        ),
        width: toRem(112),
        right: true,
        compact: true,
        sortable: true,
      },
    ],
    []
  );

  const [isOpenRemoveModal, setIsOpenRemoveModal] = useState<string>('');

  const onConfirmDetach = async () => {
    const id = isOpenRemoveModal;
    if (!id) return;
    try {
      await detachDevice(id);
      toast.success('Устройство отключено');
      setIsOpenRemoveModal('');
    } catch {
      toast.error('Не удалось отключить устройство');
    }
  };

  return (
    <>
      <DevicesTableWrapper>
        {isScreenLg ? (
          <DataTable
            columns={columns}
            data={rows}
            noDataComponent={
              <EmptyStateComponent onOpenModal={() => setIsConnectModalOpen(true)} />
            }
            customStyles={customStyles}
            responsive
            dense
            fixedHeader
            fixedHeaderScrollHeight="calc(100vh - 220px)"
          />
        ) : (
          <DevicesMobile rows={rows} onOpenModal={() => setIsConnectModalOpen(true)} />
        )}
      </DevicesTableWrapper>

      <Modal
        isOpen={isConnectModalOpen}
        centered
        style={{padding: 0, maxHeight: '90vh', width: '90%', maxWidth: '450px'}}
        onClickOverlay={() => setIsConnectModalOpen(false)}
      >
        <ConnectDeviceModalWrapper>
          <ConnectDeviceModalHeader>
            <h3>Подключение нового устройства</h3>
            <svg
              onClick={() => setIsConnectModalOpen(false)}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              width="24.000000"
              height="24.000000"
              fill="none"
            >
              <rect width="24" height="24" x="0" y="0" />
              <path
                d="M10.9401 12.0006L4.99902 17.9417L6.05968 19.0023L12.0008 13.0612L17.9418 19.0023L19.0025 17.9417L13.0614 12.0006L19.0025 6.0595L17.9418 4.99884L12.0008 10.9399L6.0597 4.99884L4.99904 6.0595L10.9401 12.0006Z"
                fill="rgb(16,24,40)"
                fillRule="evenodd"
              />
            </svg>
          </ConnectDeviceModalHeader>
          <ConnectDeviceModalSubtitle>
            Выберите улей, к которому хотите подключить устройство
          </ConnectDeviceModalSubtitle>
          <SearchInputWrapper>
            <SearchIcon />
            <Input
              placeholder="Поиск по номеру улья"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="m"
            />
          </SearchInputWrapper>
          {isLoading ? (
            <EmptyState>Загрузка...</EmptyState>
          ) : filteredHives.length === 0 ? (
            <EmptyState>Ульи не найдены</EmptyState>
          ) : (
            <HivesList>
              {filteredHives.map((hive) => (
                <HiveItem
                  key={hive.id}
                  $selected={selectedHive?.id === hive.id}
                  onClick={() => handleHiveClick(hive)}
                >
                  <HiveItemIcon>
                    <HiveIcon />
                  </HiveItemIcon>
                  <HiveItemText $selected={selectedHive?.id === hive.id}>
                    Улей №{hive.number}
                  </HiveItemText>
                </HiveItem>
              ))}
            </HivesList>
          )}
          <ModalFooter>
            <Button
              size="l"
              variant="ghost"
              colorScheme="neutral"
              label="Отменить"
              onClick={() => setIsConnectModalOpen(false)}
            />
            <Button
              size="l"
              colorScheme="accent"
              label="Далее"
              onClick={handleNext}
              disabled={!selectedHive}
            />
          </ModalFooter>
        </ConnectDeviceModalWrapper>
      </Modal>
      <Modal
        isOpen={!!isOpenRemoveModal}
        centered
        style={{padding: 0, maxHeight: '100%', width: '90%', maxWidth: '450px'}}
        onClickOverlay={() => setIsOpenRemoveModal('')}
      >
        <DeviceRemoveContent>
          <DeviceRemoveContentColF>
            <h3>Вы уверены что хотите отключить устройство?</h3>
            <svg
              onClick={() => setIsOpenRemoveModal('')}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              width="24.000000"
              height="24.000000"
              fill="none"
            >
              <rect
                id="24 / Navigation / Close Large"
                width="24.000000"
                height="24.000000"
                x="0.000000"
                y="0.000000"
              />
              <path
                id="Union"
                d="M10.9401 12.0006L4.99902 17.9417L6.05968 19.0023L12.0008 13.0612L17.9418 19.0023L19.0025 17.9417L13.0614 12.0006L19.0025 6.0595L17.9418 4.99884L12.0008 10.9399L6.0597 4.99884L4.99904 6.0595L10.9401 12.0006Z"
                fill="rgb(16,24,40)"
                fillRule="evenodd"
              />
            </svg>
          </DeviceRemoveContentColF>
          <DeviceRemoveContentColButtons>
            <Button
              size="m"
              colorScheme="neutral"
              label="Отменить"
              onClick={() => setIsOpenRemoveModal('')}
            />
            <Button size="m" colorScheme="accent" label="Отключить" onClick={onConfirmDetach} />
          </DeviceRemoveContentColButtons>
        </DeviceRemoveContent>
      </Modal>
    </>
  );
};
